import {v4 as uuidv4} from "uuid";
import {RichTextElement} from "../components/fundamentals/CCORichText/CCORichTextHelper";
import {
    SosTextfragmentFragment
} from "../../packages/graphql-layer/src/queries/fragments/__generated__/SOSTextfragment.fragment";

/**
 * Generates textAsTree p tag from passed string
 * @remarks
 * This method is used for testing.
 *
 * @param text - string that should be contained in <p> tag of the tree
 */
export function generateTree(text: string) {
    return {
        name: "div",
        _type: "Element",
        children: [{
            name: "p",
            _type: "Element",
            children: [{
                _type: "Characters",
                data: text
            } as RichTextElement]
        } as RichTextElement]
    } as RichTextElement
}

export function getKey(cmObject: string | undefined): string {
    if (cmObject === undefined) {
        return getUniqueKey();
    } else {
        return cmObject;
    }
}

export function getUniqueKey(): string {
    return uuidv4();
}

export function getUniqueKeys(count: number): string[] {
    let keys: string[] = [];
    for (let i = 0; i < count; i++) {
        keys.push(uuidv4());
    }
    return keys;
}

/**
 * Counts the number of rich text elements in the given node.
 * @param n - the starting node
 * @param score - the counter
 */
export function countRichTextElements(n: RichTextElement, score: number = 0) {
    if (!n || !n.children) {
        return 1;
    } else {
        n.children.forEach(function (c) {
            let r = countRichTextElements(c, score);
            score += r;
        });
        return score;
    }
}

export function checkStringProperty(property: any): string {
    if (!property) {
        return "";
    }
    return property;
}

export function formatDate(date: string) {
    return new Date(Date.parse(date.split('T')[0])).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "long",
        year: "numeric"
    })
}

export function stripTreeText(text: RichTextElement) {
    return text?.children && getAllTexts(text, []).join(' ') || '';
}

function getAllTexts(richText: RichTextElement, texts: string[]): string[] {
    richText.children?.forEach((child: any) => {
        texts.push(child.data);
        child._type !== 'Characters' && child.children && getAllTexts(child, texts);
    });
    return texts;
}

export function toCommaSeparatedDecimal(value: number) {
    return value.toLocaleString('de-DE', {
        minimumFractionDigits: 2, maximumFractionDigits: 2,
    })
}

export function truncateTree(tree: RichTextElement, chars: number) {
    let retval = stripTreeText(tree)
    if (retval && retval.length > chars) {
        retval = retval.substring(0, chars) + "..."
    }
    return retval
}

/**
 * Converts gallery fetched by doc types like P013News and P014News to pictures collection
 */
export function mapDoctypesGalleryToCollection(gallery: any, id: string) {
    return {
        id: id,
        items: gallery.map((item: any) => {
            return {picture: item, detailText: item?.detailText}
        })
    }
}

export const getContainerWidth = (
    selectedContentWidth?: string,
    defaultWidth: string = 'medium'
): string => {
    if (!selectedContentWidth || (selectedContentWidth !== 'small' && selectedContentWidth !== 'medium')) {
        return defaultWidth;
    }
    return selectedContentWidth;
};

/**
 * Retrieves the text from the given SosTextfragmentFragment object.
 * If the detailText is available and not empty, it is returned,
 * otherwise the teaserText is returned.
 *
 * @param {SosTextfragmentFragment} textFragment - The text fragment object to extract text from.
 *
 * @return {string} - The extracted text from the text fragment object.
 */
export function getTextFromSosTextFragment(textFragment: SosTextfragmentFragment) {
    if (textFragment.detailText) {
        const text = stripTreeText(textFragment.detailText?.textAsTree);
        if (text && text.trim().length > 0) {
            return textFragment.detailText;
        } else {
            return textFragment.teaserText
        }
    } else {
        return textFragment.teaserText
    }
}

export function getDataType(contentType: string): string {
    contentType = contentType.replace('vnd.openxmlformats-officedocument.wordprocessingml.document', 'docx');
    contentType = contentType.replace('vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'xlsx');
    contentType = contentType.replace('vnd.openxmlformats-officedocument.presentationml.presentation', 'pptx');
    const result = contentType.split('/')
    return result && result.length > 1 ? result[1].toUpperCase() : '';
}

export function getCypressDataProperty(item: any) {
    if (!item.type) {
        return '';
    }
    return item.type.replace('SOS', '').replace('CM', '');
}